import {
  Component,
  output,
  input,
  signal,
  effect,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  forwardRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { VOneInputComponent } from '@shared-components';

@Component({
  selector: 'v-one-search',
  imports: [ReactiveFormsModule, forwardRef(() => VOneInputComponent)],
  templateUrl: './v-one-search.component.html',
  styleUrls: ['./v-one-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneSearchComponent {
  private readonly destroyRef = inject(DestroyRef);

  placeholder = input<string>('Search...');
  debounceTime = input<number>(1000);
  disabled = input<boolean>(false);
  initialValue = input<string>('');

  searchChange = output<string>();

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  readonly isSearching = signal<boolean>(false);

  constructor() {
    this.setupInitialValue();
    this.setupDisabledState();
    this.setupSearchControl();
  }

  private setupInitialValue(): void {
    effect(() => {
      const initial = this.initialValue();
      if (initial) {
        this.searchControl.setValue(initial, { emitEvent: false });
      }
    });
  }

  private setupDisabledState(): void {
    effect(() => {
      const isDisabled = this.disabled();
      if (isDisabled) {
        this.searchControl.disable({ emitEvent: false });
      } else {
        this.searchControl.enable({ emitEvent: false });
      }
    });
  }

  private setupSearchControl(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounceTime()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.searchChange.emit(value);
        this.isSearching.set(false);
      });
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.searchChange.emit('');
  }
}
